import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import AuthorNameLink from "./shared/AuthorNameLink";

/**
 * Dedicated article reading page (LinkedIn-style).
 */
export default function ArticleDetail({ user, posts }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const post = useMemo(() => {
    const list = Array.isArray(posts) ? posts : [];
    return list.find((p) => String(p.id) === String(id)) || null;
  }, [posts, id]);

  if (!post) {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Article not found.</p>
        <button type="button" onClick={() => navigate("/home")}>
          Back to home
        </button>
      </div>
    );
  }

  const html = sanitizeHtml(post.content || post.text || "");
  const created = post.created_at
    ? new Date(post.created_at).toLocaleString()
    : "";

  return (
    <div
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: "24px 16px 48px",
        background: "#fff",
        minHeight: "100vh",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#0284c7",
          cursor: "pointer",
          fontSize: 14,
          marginBottom: 16,
          padding: 0,
        }}
      >
        ← Back
      </button>

      {post.cover_image && (
        <img
          src={post.cover_image}
          alt=""
          style={{
            width: "100%",
            maxHeight: 360,
            objectFit: "cover",
            borderRadius: 12,
            marginBottom: 20,
            display: "block",
          }}
        />
      )}

      <div
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "#0369a1",
          background: "#e0f2fe",
          padding: "3px 8px",
          borderRadius: 4,
          marginBottom: 10,
        }}
      >
        Article
      </div>

      <h1
        style={{
          fontSize: 32,
          fontWeight: 700,
          color: "#0f172a",
          lineHeight: 1.25,
          margin: "0 0 16px",
        }}
      >
        {post.title}
      </h1>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 28,
          paddingBottom: 16,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <img
          src={resolveAvatarUrl(post)}
          alt=""
          onError={handleAvatarError}
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>
            <AuthorNameLink user={post} style={{ fontWeight: 600 }} />
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {created}
            {post.category ? ` · ${post.category}` : ""}
            {post.country ? ` · ${post.country}` : ""}
          </div>
        </div>
      </div>

      <article
        className="cool-article-body"
        style={{
          fontSize: 17,
          lineHeight: 1.7,
          color: "#1e293b",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <style>{`
        .cool-article-body h2 { font-size: 1.4em; margin: 1.2em 0 0.5em; color: #0f172a; }
        .cool-article-body h3 { font-size: 1.2em; margin: 1em 0 0.4em; color: #0f172a; }
        .cool-article-body p { margin: 0 0 1em; }
        .cool-article-body ul, .cool-article-body ol { margin: 0 0 1em; padding-left: 1.4em; }
        .cool-article-body a { color: #0284c7; }
        .cool-article-body blockquote {
          border-left: 3px solid #cbd5e1;
          margin: 1em 0;
          padding-left: 1em;
          color: #475569;
        }
      `}</style>

      <div style={{ marginTop: 32 }}>
        <Link
          to={`/home/post/${post.id}`}
          style={{ fontSize: 14, color: "#64748b" }}
        >
          View as post with comments →
        </Link>
      </div>
    </div>
  );
}
